import type { RecipeIngredient } from "./recipes";

export type QuantityDimension = "weight" | "volume" | "temperature" | "length" | "count";

export type Quantity = {
  value: number;
  unit: string;
  dimension: QuantityDimension;
};

export function scaleQuantity(quantity: Quantity, factor: number): Quantity {
  return {
    ...quantity,
    value: roundQuantityValue(quantity.value * factor)
  };
}

export function roundQuantityValue(value: number): number {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? rounded : rounded;
}

export function formatQuantity(quantity: Quantity): string {
  const valueText = Number.isInteger(quantity.value) ? String(quantity.value) : String(quantity.value);
  if (quantity.dimension === "count" && quantity.unit.length === 0) {
    return valueText;
  }
  if (quantity.unit.length === 0) {
    return valueText;
  }
  return `${valueText} ${quantity.unit}`;
}

export function formatIngredientDisplay(ingredient: RecipeIngredient, scaleFactor = 1): string {
  if (ingredient.quantity === undefined) {
    return ingredient.name;
  }

  const quantity =
    scaleFactor === 1 ? ingredient.quantity : scaleQuantity(ingredient.quantity, scaleFactor);
  const amount = formatQuantity(quantity);
  return `${amount} ${ingredient.name}`;
}

export function scaleRecipeIngredients(
  ingredients: readonly RecipeIngredient[],
  baseServings: number,
  targetServings: number
): RecipeIngredient[] {
  if (baseServings < 1 || targetServings < 1 || baseServings === targetServings) {
    return [...ingredients];
  }

  const factor = targetServings / baseServings;
  return ingredients.map((ingredient) => {
    if (ingredient.quantity === undefined) {
      return ingredient;
    }
    return {
      ...ingredient,
      quantity: scaleQuantity(ingredient.quantity, factor)
    };
  });
}
